(() => {
  'use strict';

  /**
   * The storefront's one reader of the market's delivery configuration, `GET /api/v1/delivery-estimates` of the
   * Checkout API. The Checkout API owns delivery prices, free-shipping thresholds and the payments each delivery
   * accepts (Platform → delivery methods); the theme only shows them. One request per page serves every consumer:
   * the delivery estimate, the delivery promise of the product detail and the cart, the information bar and the cart in the header, the free-shipping offer of the home page, the free-shipping progress in the cart and the cart drawer, the payment
   * methods in the footer and the cart, and the same-day dispatch of the shipping benefit. When the API does not answer, those hints stay hidden: a missing hint is
   * better than a threshold or a payment method the checkout would not honour.
   *
   * The market, its currency and the API origin come from the config element `bestio-delivery-options.liquid`
   * renders once per page from Liquid `localization`.
   */
  const REQUEST_TIMEOUT_MS = 10000;
  // The answer is kept in sessionStorage, so browsing the catalogue does not ask the Checkout API on every page and
  // does not use up the rate limit it shares with the checkout (BEST-287). Five minutes keep the cut-off time of the
  // delivery estimate honest; a failed answer is never kept.
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const CACHE_KEY_PREFIX = 'bestio:delivery-options:v1:';
  // Payment codes of the Checkout API (bestio-hub CommerceCodes.SupportedPaymentMethod) the storefront shows.
  const PAYMENT_CODES = Object.freeze(['online', 'bank', 'cod']);

  /**
   * The lowest free-shipping threshold across the market's deliveries, or null when no delivery has one. A threshold
   * of 0 is real: the server makes that delivery free for every cart (`merchandiseAmount >= threshold`).
   */
  function freeShippingThreshold(options) {
    const thresholds = (Array.isArray(options) ? options : [])
      .map(option => option?.freeShippingFrom)
      .filter(threshold => threshold != null && threshold !== '')
      .map(Number)
      .filter(threshold => Number.isFinite(threshold) && threshold >= 0);
    return thresholds.length ? Math.min(...thresholds) : null;
  }

  /** The currency the market's deliveries are priced in (the server returns only the market currency). */
  function currencyOf(options) {
    const code = (Array.isArray(options) ? options : []).map(option => option?.currencyCode).find(Boolean);
    return code ? String(code).toUpperCase() : null;
  }

  /** Every payment the checkout offers with at least one delivery of the market, in the storefront's order. */
  function paymentCodes(options) {
    const offered = new Set();
    for (const option of Array.isArray(options) ? options : []) {
      for (const code of Array.isArray(option?.allowedPaymentCodes) ? option.allowedPaymentCodes : []) {
        offered.add(String(code).trim().toLowerCase());
      }
    }
    return PAYMENT_CODES.filter(code => offered.has(code));
  }

  /**
   * Where the cart stands against the threshold. The server measures it on the goods after discounts, and in the
   * Shopify cart that is `cart.total_price`: the cart has no delivery and no payment fee yet.
   */
  function freeShippingProgress(cartAmount, threshold) {
    const amount = Number(cartAmount);
    if (!Number.isFinite(amount) || amount < 0 || threshold == null || !(threshold >= 0)) return null;
    if (amount >= threshold) return {reached: true, remaining: 0, percent: 100};
    return {reached: false, remaining: threshold - amount, percent: Math.min(100, Math.round((amount / threshold) * 100))};
  }

  // Whole amounts without decimals, the way the storefront shows prices (1 199 Kč, 49 €); same rule as the checkout.
  function formatMoney(value, currencyCode, locale) {
    const amount = Number(value || 0);
    return new Intl.NumberFormat(locale, {style: 'currency', currency: currencyCode, minimumFractionDigits: Number.isInteger(amount) ? 0 : 2}).format(amount);
  }

  /**
   * The order cut-off for same-day dispatch the shipping benefit promises (BEST-612): the earliest cut-off of the
   * market's deliveries (`orderByLocalTime`, Platform → delivery methods, the same setting the checkout's delivery
   * dates and the feeds' handling time come from). The earliest, because the promise has to hold whichever delivery
   * the customer picks. Null when no delivery has a valid HH:MM cut-off; the benefit then keeps its text without a time.
   */
  function dispatchCutoff(options) {
    const cutoffs = (Array.isArray(options) ? options : [])
      .map(option => String(option?.orderByLocalTime ?? '').trim())
      .filter(time => /^([01]\d|2[0-3]):[0-5]\d$/.test(time))
      .sort();
    return cutoffs[0] ?? null;
  }

  /**
   * The cheapest delivery price of the market, for the "otherwise from" line of the free-shipping offer on the home page.
   * Null when no delivery has a valid price; 0 is a real price (a free delivery).
   */
  function lowestDeliveryPrice(options) {
    const prices = (Array.isArray(options) ? options : [])
      .map(option => option?.price)
      .filter(price => price != null && price !== '')
      .map(Number)
      .filter(price => Number.isFinite(price) && price >= 0);
    return prices.length ? Math.min(...prices) : null;
  }

  /** The carriers of the market's deliveries (Checkout API carrier codes), each once, in the order of the deliveries. */
  function carriersOf(options) {
    const codes = (Array.isArray(options) ? options : []).map(option => String(option?.carrier ?? '').trim().toLowerCase()).filter(Boolean);
    return [...new Set(codes)];
  }

  // "kartou, převodem a na dobírku" in the language of the page; a runtime without Intl.ListFormat joins with commas.
  function listText(items, language) {
    try { return new Intl.ListFormat(language, {style: 'long', type: 'conjunction'}).format(items); } catch (_) { return items.join(', '); }
  }

  const api = {freeShippingThreshold, currencyOf, paymentCodes, freeShippingProgress, formatMoney, dispatchCutoff, lowestDeliveryPrice, carriersOf, listText};

  const config = document.querySelector('[data-bestio-delivery-options]');
  const locale = document.documentElement.lang || 'cs';
  const settings = {
    apiBaseUrl: String(config?.dataset.checkoutApiBaseUrl || '').replace(/\/$/, ''),
    marketCode: String(config?.dataset.marketCountryCode || 'CZ').toUpperCase(),
    languageCode: locale.slice(0, 2).toLowerCase(),
  };

  const cacheKey = `${CACHE_KEY_PREFIX}${settings.apiBaseUrl}|${settings.marketCode}|${settings.languageCode}`;
  function cached(now = Date.now()) {
    try {
      const entry = JSON.parse(window.sessionStorage.getItem(cacheKey) || 'null');
      if (entry && Array.isArray(entry.options) && now - entry.storedAt >= 0 && now - entry.storedAt < CACHE_TTL_MS) return entry.options;
    } catch (_) { /* no storage or a broken entry: ask the API */ }
    return null;
  }
  function remember(options) {
    try { window.sessionStorage.setItem(cacheKey, JSON.stringify({storedAt: Date.now(), options})); } catch (_) { /* storage full or blocked */ }
  }

  let request;
  /** The market's delivery options; one request per page at most and none while the session keeps a fresh answer,
   *  an empty list when the API is unreachable. */
  function load() {
    if (request) return request;
    if (!settings.apiBaseUrl) {
      request = Promise.resolve([]);
      return request;
    }
    const kept = cached();
    if (kept) {
      request = Promise.resolve(kept);
      return request;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const params = new URLSearchParams({marketCode: settings.marketCode, languageCode: settings.languageCode});
    request = fetch(`${settings.apiBaseUrl}/api/v1/delivery-estimates?${params}`, {headers: {Accept: 'application/json'}, signal: controller.signal})
      .then(response => {
        if (!response.ok) throw new Error(`Delivery options answered ${response.status}.`);
        return response.json();
      })
      .then(options => {
        const list = Array.isArray(options) ? options : [];
        remember(list);
        return list;
      })
      .catch(error => {
        console.warn('Bestio delivery options are unavailable; delivery and payment hints stay hidden.', {marketCode: settings.marketCode, reason: error?.message});
        return [];
      })
      .finally(() => window.clearTimeout(timer));
    return request;
  }

  // --- Free-shipping progress (snippets/free-shipping.liquid) -----------------------------------------------------
  function renderFreeShipping(element, options) {
    const threshold = freeShippingThreshold(options);
    const currencyCode = currencyOf(options);
    const cartCurrency = String(element.dataset.cartCurrency || '').toUpperCase();
    // Minor units from Liquid; the API speaks in major units.
    const progress = currencyCode && cartCurrency === currencyCode
      ? freeShippingProgress(Number(element.dataset.cartAmount) / 100, threshold)
      : null;
    element.hidden = !progress;
    if (!progress) return;
    element.querySelector('[data-free-shipping-progress]').hidden = progress.reached;
    element.querySelector('[data-free-shipping-success]').hidden = !progress.reached;
    if (progress.reached) return;
    element.style.setProperty('--bestio-free-shipping-progress', `${progress.percent}%`);
    element.querySelector('[data-free-shipping-remaining]').textContent = formatMoney(progress.remaining, currencyCode, locale);
    const goal = element.querySelector('[data-free-shipping-goal]');
    if (goal) goal.textContent = String(goal.dataset.text || '').replace('__amount__', formatMoney(threshold, currencyCode, locale));
  }

  // --- Payment methods (snippets/bestio-payment-methods.liquid) ---------------------------------------------------
  function renderPaymentMethods(element, options) {
    const offered = paymentCodes(options);
    let shown = 0;
    element.querySelectorAll('[data-payment-method]').forEach(item => {
      // A method without anything to show (no card brand enabled in the shop) stays hidden.
      item.hidden = !offered.includes(item.dataset.paymentMethod) || !item.childElementCount;
      if (!item.hidden) shown += 1;
    });
    element.hidden = shown === 0;
  }

  // --- Same-day dispatch of the shipping benefit (snippets/bestio-benefit.liquid) --------------------------------
  function renderDispatchCutoff(element, options) {
    const time = dispatchCutoff(options);
    const template = element.dataset.cutoffText || '';
    if (time && template.includes('__time__')) element.textContent = template.replace('__time__', time);
  }

  // --- Free-shipping offer of the home page hero (sections/bestio-catalog-hero.liquid) --------------------------
  // The offer names the threshold and the cheapest delivery below it; it stays hidden while the market has no threshold.
  function renderFreeShippingOffer(element, options) {
    const threshold = freeShippingThreshold(options);
    const currencyCode = currencyOf(options);
    element.hidden = threshold == null || !currencyCode;
    if (element.hidden) return;
    const title = element.querySelector('[data-free-shipping-offer-title]');
    title.textContent = threshold > 0
      ? String(title.dataset.textFrom || '').replace('__amount__', formatMoney(threshold, currencyCode, locale))
      : String(title.dataset.textAlways || '');
    const price = element.querySelector('[data-free-shipping-offer-price]');
    const lowest = lowestDeliveryPrice(options);
    price.hidden = !(threshold > 0 && lowest > 0);
    if (!price.hidden) price.textContent = String(price.dataset.text || '').replace('__price__', formatMoney(lowest, currencyCode, locale));
  }

  // --- Header cart (snippets/bestio-header-cart.liquid) ----------------------------------------------------------
  // The ring and the line of the way to free shipping, measured like the cart's progress; without a threshold (or in
  // another currency) the line stays hidden and the ring empty, the cart keeps its icon, count and total.
  function renderHeaderCart(element, options) {
    const threshold = freeShippingThreshold(options);
    const currencyCode = currencyOf(options);
    const cartCurrency = String(element.dataset.cartCurrency || '').toUpperCase();
    const progress = currencyCode && cartCurrency === currencyCode
      ? freeShippingProgress(Number(element.dataset.cartAmount) / 100, threshold)
      : null;
    element.style.setProperty('--bestio-free-shipping-progress', progress ? `${progress.percent}%` : '0%');
    element.dataset.freeShipping = progress ? (progress.reached ? 'reached' : 'progress') : '';
    const ship = element.querySelector('[data-header-cart-ship]');
    ship.hidden = !progress;
    if (!progress) return;
    const remaining = element.querySelector('[data-header-cart-remaining]');
    remaining.hidden = progress.reached;
    element.querySelector('[data-header-cart-free]').hidden = !progress.reached;
    if (!progress.reached) remaining.textContent = String(remaining.dataset.text || '').replace('__amount__', formatMoney(progress.remaining, currencyCode, locale));
  }

  // --- Free-shipping ring of the cart in the bottom navigation (snippets/bestio-mobile-bottom-nav.liquid) ----------
  // The same progress as the header cart; the navigation is not re-rendered, so it calls refresh() after a cart change.
  function renderCartRing(element, options) {
    const currencyCode = currencyOf(options);
    const cartCurrency = String(element.dataset.cartCurrency || '').toUpperCase();
    const progress = currencyCode && cartCurrency === currencyCode
      ? freeShippingProgress(Number(element.dataset.cartAmount) / 100, freeShippingThreshold(options))
      : null;
    element.style.setProperty('--bestio-free-shipping-progress', progress ? `${progress.percent}%` : '0%');
    element.dataset.freeShipping = progress ? (progress.reached ? 'reached' : 'progress') : '';
  }

  // --- Information bar above the header (sections/bestio-info-bar.liquid) ------------------------------------------
  // The carriers and the payments the market's deliveries accept; without them the item stays hidden. Carrier names and
  // payment labels come translated from Liquid. The delivery estimate of the bar fills itself (bestio-delivery-estimates.js).
  function readJson(text) {
    try { return JSON.parse(text || '{}') || {}; } catch (_) { return {}; }
  }
  function renderInfoBar(element, options) {
    const delivery = element.querySelector('[data-bestio-info-delivery]');
    if (!delivery) return;
    const names = readJson(element.dataset.carrierNames);
    const labels = readJson(element.dataset.paymentLabels);
    const carriers = carriersOf(options).map(code => names[code] || code.toUpperCase());
    delivery.hidden = carriers.length === 0;
    delivery.querySelector('[data-bestio-info-carriers]').textContent = carriers.join(', ');
    const methods = paymentCodes(options).map(code => labels[code]).filter(Boolean);
    const payments = delivery.querySelector('[data-bestio-info-payments]');
    payments.hidden = methods.length === 0;
    if (methods.length) payments.textContent = String(delivery.dataset.text || '').replace('__methods__', listText(methods, settings.languageCode));
  }

  // --- Delivery promise of the product detail and the cart (snippets/bestio-delivery-promise.liquid, BEST-658) ------
  // The summary the checkout's delivery step gives the pickup group (bestio-checkout.js groupSummary): its earliest
  // delivery date in the words and the short date of the step (group_estimate, SHORT_DATE_FORMAT) and its price. The price
  // is the Checkout API's for the promise's goods (`goodsAmount`): the server decides free shipping, the theme compares no
  // threshold. Free only when every delivery of the group is free, otherwise the cheapest, like the step. A market without
  // pickup points names the deliveries to the address the same way. One request per amount, kept for the session.
  const pricedRequests = new Map();
  function loadPriced(amount) {
    const key = `${cacheKey}|${amount}`;
    if (pricedRequests.has(key)) return pricedRequests.get(key);
    let kept = null;
    try {
      const entry = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      const age = Date.now() - (entry?.storedAt ?? -1);
      if (entry && Array.isArray(entry.options) && age >= 0 && age < CACHE_TTL_MS) kept = entry.options;
    } catch (_) { /* no storage or a broken entry: ask the API */ }
    if (kept || !settings.apiBaseUrl) {
      const done = Promise.resolve(kept || []);
      pricedRequests.set(key, done);
      return done;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const params = new URLSearchParams({marketCode: settings.marketCode, languageCode: settings.languageCode, goodsAmount: String(amount)});
    const pending = fetch(`${settings.apiBaseUrl}/api/v1/delivery-estimates?${params}`, {headers: {Accept: 'application/json'}, signal: controller.signal})
      .then(response => {
        if (!response.ok) throw new Error(`Delivery options answered ${response.status}.`);
        return response.json();
      })
      .then(options => {
        const list = Array.isArray(options) ? options : [];
        try { window.sessionStorage.setItem(key, JSON.stringify({storedAt: Date.now(), options: list})); } catch (_) { /* storage full or blocked */ }
        return list;
      })
      .catch(() => [])
      .finally(() => window.clearTimeout(timer));
    pricedRequests.set(key, pending);
    return pending;
  }
  function parseDate(raw) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw || ''));
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
  }
  const SHORT_DATE_FORMAT = settings.languageCode === 'en'
    ? new Intl.DateTimeFormat('en-GB', {weekday: 'short', day: 'numeric', month: 'short'})
    : new Intl.DateTimeFormat(locale, {weekday: 'short', day: 'numeric', month: 'numeric'});

  function earliestDelivery(options) {
    return (Array.isArray(options) ? options : [])
      .map(option => parseDate(option?.estimatedDeliveryFrom))
      .filter(Boolean)
      .sort((left, right) => left - right)[0] ?? null;
  }

  /** The group's price as the checkout's step says it: free when every delivery is free, otherwise the cheapest; null
   *  without a valid price. The prices are the API's for the promise's goods, so nothing is compared here. */
  function groupPrice(options) {
    const prices = (Array.isArray(options) ? options : [])
      .filter(option => option?.price != null && option.price !== '' && Number.isFinite(Number(option.price)) && Number(option.price) >= 0)
      .map(option => Number(option.price));
    if (!prices.length) return null;
    return {free: prices.every(price => price === 0), cheapest: Math.min(...prices)};
  }

  function deliveryGroup(options) {
    const pickup = (Array.isArray(options) ? options : []).filter(option => String(option?.deliveryMode || '').toLowerCase() === 'pickup');
    return {group: pickup.length ? pickup : (Array.isArray(options) ? options : []), kind: pickup.length ? 'pickup' : 'shipping'};
  }

  function renderDeliveryPromise(element, options) {
    const texts = readJson(element.dataset.texts);
    const {group, kind} = deliveryGroup(options);
    const date = earliestDelivery(group);
    const currencyCode = currencyOf(options);
    const amount = Number(element.dataset.amount) / 100;
    const sameCurrency = currencyCode && String(element.dataset.currency || '').toUpperCase() === currencyCode;
    element.hidden = !date || !sameCurrency || !Number.isFinite(amount) || amount < 0 || !texts.date;
    if (element.hidden) return;
    const dateText = String(texts.date).replace('{date}', SHORT_DATE_FORMAT.format(date));
    const target = element.querySelector('[data-bestio-delivery-promise-text]');
    target.textContent = dateText;
    // The price follows once the API priced the goods; without that answer the date stands alone.
    return loadPriced(amount).then(priced => {
      const price = groupPrice(deliveryGroup(priced).group.filter(option => String(option?.currencyCode || '').toUpperCase() === currencyCode));
      const parts = [dateText];
      if (price?.free && texts[`${kind}Free`]) parts.push(texts[`${kind}Free`]);
      else if (price && texts[kind]) parts.push(String(texts[kind]).replace('__price__', formatMoney(price.cheapest, currencyCode, locale)));
      target.textContent = parts.join(' · ');
    });
  }

  const CONSUMERS = Object.freeze([
    ['[data-bestio-delivery-promise]', renderDeliveryPromise],
    ['[data-bestio-free-shipping]', renderFreeShipping],
    ['[data-bestio-free-shipping-offer]', renderFreeShippingOffer],
    ['[data-bestio-info-bar]', renderInfoBar],
    ['[data-bestio-header-cart]', renderHeaderCart],
    ['[data-bestio-cart-ring]', renderCartRing],
    ['[data-bestio-payment-methods]', renderPaymentMethods],
    ['[data-bestio-dispatch-cutoff]', renderDispatchCutoff],
  ]);

  function renderAll(options, scope = document) {
    for (const [selector, render] of CONSUMERS) scope.querySelectorAll(selector).forEach(element => render(element, options));
  }

  /** Renders the consumers inside `scope` again, after a page script changed their data (the bottom navigation's cart). */
  function refresh(scope = document) {
    if (!config) return Promise.resolve();
    return load().then(options => renderAll(options, scope));
  }

  window.BestioDeliveryOptions = Object.freeze({...api, load, refresh});

  if (!config) return;
  load().then(options => {
    renderAll(options);
    // The cart and the cart drawer are re-rendered through the Section Rendering API after every change; the new
    // markup comes hidden from Liquid and is filled from the options already loaded.
    const selector = CONSUMERS.map(([consumer]) => consumer).join(',');
    new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches(selector)) renderAll(options, node.parentElement || document);
          else if (node.querySelector(selector)) renderAll(options, node);
        }
      }
    }).observe(document.body, {childList: true, subtree: true});
  });
})();
